import classNames from "classnames";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BsTrash, BsUpload } from "react-icons/bs";
import { MdOpenInNew, MdOutlineRadioButtonChecked } from "react-icons/md";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";
import ImageJustification from "shared/assets/images/lmf/lmf_justificative.png";
import HelpYellow from "shared/components/SVG/LMF/HelpYellow";
import { NAME_SPACES } from "shared/locales/constants";
import { bytesToMB } from "utils/helpers/files";
import ActionButtons from "../Components/ActionButtons";
import Button from "../Components/Button";
import CustomModal from "../Components/CustomModal";
import Head from "../Components/Head";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import validation from "./validation";

const View = ({ initialValues, onSubmit, locked, translate, onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const [open, setOpen] = useState(false);
  const [openChild, setOpenChild] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { isValid, handleSubmit, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues,
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        get(initialValues, "includedChildren", []),
      ),
    });

  const includedChildren = get(values, "includedChildren", []);
  if (!includedChildren || !Array.isArray(includedChildren)) {
    return <>Chargement...</>;
  }

  const onFileRemove = async ({ uid: id }) => {
    if (id.indexOf("AUTO") !== -1) return;
  };

  return (
    <>
      <CustomModal
        open={open}
        setOpen={setOpen}
        width={500}
        closeText={translate.MODAL.CLOSE}
      >
        <div className="with--link">
          <img src={ImageJustification} alt="Justification" />
          <Link
            to={
              "https://www.ameli.fr/assure/adresses-et-contacts/l-obtention-d-un-document/obtenir-une-attestation/obtenir-une-attestation-de-droits"
            }
            className="lmf__modal--link"
            target="_blank"
          >
            <span>{translate.MODAL.LINK}</span>
            <MdOpenInNew size={20} color="#FEC212" />
          </Link>
        </div>
      </CustomModal>

      <CustomModal
        open={openChild}
        setOpen={setOpenChild}
        title={translate.MODAL_INFO.TITLE}
        TRANSLATION={translate}
        width={400}
        closeText={translate.MODAL_INFO.CLOSE}
      >
        <ul className="lmf__list">
          <li>
            <MdOutlineRadioButtonChecked />
            <span>{translate.MODAL_INFO.LIST.ITEM_1}</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked />
            <span>{translate.MODAL_INFO.LIST.ITEM_2}</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked />
            <span>{translate.MODAL_INFO.LIST.ITEM_3}</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked />
            <span>{translate.MODAL_INFO.LIST.ITEM_4}</span>
          </li>
        </ul>
        {/* <span>Les documents acceptés sont : </span>
        <ul className="lmf__list">
          <li>
            <MdOutlineRadioButtonChecked />
            <span>Carte d'étudiant en cours de validité</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked />
            <span>Certificat de scolarité</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked />
            <span>Contrat d'apprentissage</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked />
            <span>Carte d'invalidité ou notification CDAPH (handicap)</span>
          </li>
          <li>
            <MdOutlineRadioButtonChecked />
            <span>Attestation France Travail (sans activité)</span>
          </li>
        </ul> */}
      </CustomModal>

      <Layout>
        <Head
          description={translate.DESCRIPTION}
          title={translate.TITLE}
          onBack={onBack}
        />

        <div className="lmf__form grid--1 gap-24">
          {includedChildren.map((child, index) => {
            const isOver18 = child.age >= 18;
            const isOver16 = child.age >= 16;
            const title =
              index === 0
                ? "Premier enfant"
                : index === 1
                  ? "Deuxième enfant"
                  : index === 2
                    ? "Troisième enfant"
                    : `${index + 1}ème enfant`;

            const healthInsuranceRightsCertificateKey = `healthInsuranceRightsCertificate_${child.userId}`;
            const childDependentProofKey = `dependentProof_${child.userId}`;
            const bankAccountDetailsForPaymentKey = `bankAccountDetailsForPayment_${child.userId}`;

            const renderFileList = (key) =>
              get(values, key, []).map(({ name, size }, i) => (
                <small key={`${key}-${i}`}>
                  {name}
                  {size ? ` - ${bytesToMB(size)}MB` : ""}
                </small>
              ));

            return (
              <div key={child.userId} className="justification__section--group">
                <h3 className="justification__section--title">{title}</h3>

                <div className="justification__section--child">
                  <Input
                    type="file"
                    variant="uploader"
                    listType="picture"
                    onUpload={(fileList) => {
                      const sanitized = fileList.map((file) => ({
                        ...file,
                        name: file.name || "document.pdf",
                      }));
                      setFieldValue(
                        healthInsuranceRightsCertificateKey,
                        sanitized,
                      );
                    }}
                    validTypes={translate.DOCUMENTS.ALLOWED_FILES_TYPE}
                    onFileRemove={onFileRemove}
                    fileList={get(
                      values,
                      healthInsuranceRightsCertificateKey,
                      [],
                    )}
                  >
                    <div className="upload">
                      <div className="label">
                        <span>
                          {
                            translate.DOCUMENTS.TYPES
                              .HEALTH_INSURANCE_RIGHTS_CERTIFICATE
                          }
                        </span>
                        <div className="infos">
                          <span className="required">
                            {isOver16 && (
                              <small className="required">Obligatoire</small>
                            )}
                            <HelpYellow
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpen(true);
                              }}
                              size={24}
                            />
                          </span>
                          <small className="hint">Datant de -3 mois</small>
                        </div>

                        {renderFileList(healthInsuranceRightsCertificateKey)}
                      </div>
                      <Button
                        type={
                          get(values, healthInsuranceRightsCertificateKey, [])
                            .length
                            ? "danger-outline"
                            : "white"
                        }
                        size="middle"
                        onClick={(e) => {
                          if (
                            get(values, healthInsuranceRightsCertificateKey, [])
                              .length
                          ) {
                            e.stopPropagation();
                            setFieldValue(
                              healthInsuranceRightsCertificateKey,
                              [],
                            );
                          }
                        }}
                      >
                        {get(values, healthInsuranceRightsCertificateKey, [])
                          .length ? (
                          <BsTrash size={20} />
                        ) : (
                          <BsUpload size={20} />
                        )}
                      </Button>
                    </div>
                  </Input>
                  {isOver16 &&
                    get(
                      formProps.errors,
                      healthInsuranceRightsCertificateKey,
                    ) && (
                      <span className="error-message">
                        {get(
                          formProps.errors,
                          healthInsuranceRightsCertificateKey,
                        )}
                      </span>
                    )}
                </div>

                {isOver18 && (
                  <div className="justification__section--child">
                    <Input
                      type="file"
                      variant="uploader"
                      listType="picture"
                      onUpload={(fileList) => {
                        const sanitized = fileList.map((file) => ({
                          ...file,
                          name: file.name || "document.pdf",
                        }));
                        setFieldValue(childDependentProofKey, sanitized);
                      }}
                      validTypes={translate.DOCUMENTS.ALLOWED_FILES_TYPE}
                      onFileRemove={onFileRemove}
                      fileList={get(values, childDependentProofKey, [])}
                    >
                      <div className="upload">
                        <div className="label">
                          <span>
                            {translate.DOCUMENTS.TYPES.JUSTIFICATION_CHILDREN}
                          </span>
                          <div className="infos">
                            <span className="required">
                              {isOver18 && (
                                <small className="required">Obligatoire</small>
                              )}
                              <HelpYellow
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenChild(true);
                                }}
                                size={24}
                              />
                            </span>
                          </div>
                          {renderFileList(childDependentProofKey)}
                        </div>
                        <Button
                          type={
                            get(values, childDependentProofKey, []).length
                              ? "danger-outline"
                              : "white"
                          }
                          size="middle"
                          onClick={(e) => {
                            if (
                              get(values, childDependentProofKey, []).length
                            ) {
                              e.stopPropagation();
                              setFieldValue(childDependentProofKey, []);
                            }
                          }}
                        >
                          {get(values, childDependentProofKey, []).length ? (
                            <BsTrash size={20} />
                          ) : (
                            <BsUpload size={20} />
                          )}
                        </Button>
                      </div>
                    </Input>
                    {get(formProps.errors, childDependentProofKey) && (
                      <span className="error-message">
                        {get(formProps.errors, childDependentProofKey)}
                      </span>
                    )}
                  </div>
                )}
                <div className="justification__section--child">
                  <Input
                    type="file"
                    variant="uploader"
                    listType="file"
                    onUpload={(fileList) => {
                      const sanitized = fileList.map((file) => ({
                        ...file,
                        name: file.name || "document.pdf",
                      }));
                      setFieldValue(bankAccountDetailsForPaymentKey, sanitized);
                    }}
                    validTypes={translate.DOCUMENTS.ALLOWED_FILES_TYPE}
                    onFileRemove={onFileRemove}
                    fileList={get(values, bankAccountDetailsForPaymentKey, [])}
                  >
                    <div className="upload">
                      <div className="label">
                        <span>{translate.DOCUMENTS.TYPES.RIB_PRESTATION}</span>
                        {renderFileList(bankAccountDetailsForPaymentKey)}
                      </div>
                      <Button
                        type={
                          get(values, bankAccountDetailsForPaymentKey, [])
                            .length
                            ? "danger-outline"
                            : "white"
                        }
                        size="middle"
                        onClick={(e) => {
                          if (
                            get(values, bankAccountDetailsForPaymentKey, [])
                              .length
                          ) {
                            e.stopPropagation();
                            setFieldValue(bankAccountDetailsForPaymentKey, []);
                          }
                        }}
                      >
                        {get(values, bankAccountDetailsForPaymentKey, [])
                          .length ? (
                          <BsTrash size={20} />
                        ) : (
                          <BsUpload size={20} />
                        )}
                      </Button>
                    </div>
                  </Input>
                </div>
              </div>
            );
          })}
        </div>

        <ActionButtons
          buttonText={translate.NEXT}
          onNext={handleSubmit}
          disabled={!isEmpty(formProps.errors)}
          {...(isMobile && { onBack })}
          className={classNames({
            "w-100": isMobile,
          })}
        />
      </Layout>
    </>
  );
};

export default View;
